import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { deletedLine, type DeleteAudience } from '../../lib/portalAssets/deletion';
import { AWAITING_SUPPORT_DETAIL, formatBytes, formatDuration, mediaImage } from '../../lib/portalAssets/gridView';
import { TagChipInput } from './TagChipInput';
import { normalizeAssetTitle } from '../../lib/portalAssets/derived';
import { assetDisplayName } from '../../lib/portalAssets/gridView';
import {
  PENDING_BANNER,
  SCAN_LABEL,
  acceptPatch,
  applySuggestion,
  canEnrich,
  keptNote,
  proposedFields,
  type AcceptPatch,
  type EnrichDrafts,
} from '../../lib/portalAssets/enrichmentView';
import type { PortalAssetDetail, PortalAssetEnrichmentSuggestion } from '../../types/portalAsset';
import { AssetCategoryPicker, type CategoriesState } from './AssetCategoryPicker';
import { AssetImage, AssetStatusBadge } from './AssetGrid';

// ONE ASSET (spec §8 "Detail") — shared by the client page and the staff view.
// Presentational: the host owns the fetch and the save.
//
// ⚠️ The reviewer's name is whatever the TILE says. On a client tile any staff
// member is "Kyck" (Q4, ANSWERED 2026-10-07: clubs deal with the agency, and
// staff turnover should not leave names scattered through a client's history);
// that is decided in src/lib/portalAssets/tile.ts and nothing here re-derives it.
//
// Tags and categories are the edits offered here, for BOTH audiences (2026-10-10:
// the client router now serves GET /categories, so the picker has something behind
// it). An asset can be in several categories; the host passes the list's state so
// "loading", "failed" and "this brand has none" read differently.

export function formatAssetDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function reviewLine(detail: Pick<PortalAssetDetail, 'review' | 'lifecycle'>): string | null {
  if (detail.lifecycle !== 'ready') return null;
  const { status, byName, at } = detail.review;
  if (status === 'pending') return 'Not reviewed yet.';
  const when = formatAssetDate(at);
  const verb = status === 'approved' ? 'Approved' : 'Not approved';
  return [verb, byName ? `by ${byName}` : null, when ? `on ${when}` : null].filter(Boolean).join(' ') + '.';
}

export function AssetDetailView({
  detail,
  audience,
  onSaveTitle,
  titleSaving = false,
  titleError = null,
  onSaveTags,
  saving = false,
  saveError = null,
  categories,
  onSaveCategories,
  categoriesSaving = false,
  categoriesError = null,
  onImageError,
  onEnrich,
  onAcceptEnrichment,
}: {
  detail: PortalAssetDetail;
  /** Who is reading — decides the deleted line's wording (a club cannot restore; Kyck can). */
  audience: DeleteAudience;
  /** A string sets the title; null clears it (the file name is then used). */
  onSaveTitle?: (title: string | null) => void;
  titleSaving?: boolean;
  titleError?: string | null;
  onSaveTags?: (tags: string[]) => void;
  saving?: boolean;
  saveError?: string | null;
  categories?: CategoriesState;
  onSaveCategories?: (ids: string[]) => void;
  categoriesSaving?: boolean;
  categoriesError?: string | null;
  onImageError?: () => void;
  /**
   * AI SUGGESTION (Run D) - present = the button is offered. Returns a suggestion and WRITES NOTHING. ON-DEMAND
   * ONLY: it is called from the button's click handler and nowhere else (enrichmentOnDemandOnly.test.ts).
   */
  onEnrich?: () => Promise<PortalAssetEnrichmentSuggestion>;
  /** Save the suggestion (with the user's edits) as ONE patch carrying the receipt. */
  onAcceptEnrichment?: (patch: AcceptPatch) => Promise<unknown>;
}) {
  const [titleDraft, setTitleDraft] = useState(detail.title ?? '');
  const parsedTitle = normalizeAssetTitle(titleDraft);
  const titleUnchanged = parsedTitle.ok && parsedTitle.text === detail.title;
  // The chips ARE the normalised list (TagChipInput builds every one through normalizeAssetTags), so there is nothing left
  // to parse on save: what the chips show is what is sent, and what the server stores.
  const [tagList, setTagList] = useState<string[]>(detail.tags);
  const unchanged = tagList.join('\u0000') === detail.tags.join('\u0000');
  // The category draft lives HERE (the picker is controlled) so a suggestion can fill it and Accept can read it.
  const [catDraft, setCatDraft] = useState<string[]>([...detail.categoryIds]);

  // ---- AI suggestion (Run D). No effect anywhere in this block: the only trigger is the click handler.
  const [scanning, setScanning] = useState(false);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<{ s: PortalAssetEnrichmentSuggestion; before: EnrichDrafts } | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const drafts: EnrichDrafts = { title: titleDraft, tags: tagList, categoryIds: catDraft };
  const proposed = proposal ? proposedFields(proposal.s) : [];
  const pending = proposal !== null;

  const runEnrich = async () => {
    if (!onEnrich) return;
    setScanning(true);
    setEnrichError(null);
    try {
      const s = await onEnrich();
      const before = drafts;
      const next = applySuggestion(before, s);
      setTitleDraft(next.title);
      setTagList([...next.tags]);
      setCatDraft([...next.categoryIds]);
      setProposal({ s, before });
    } catch (e) {
      // On failure the fields do not move; the message is the server's.
      setEnrichError(e instanceof Error && e.message ? e.message : 'Could not get a suggestion. Try again.');
    } finally {
      setScanning(false);
    }
  };
  const discard = () => {
    if (!proposal) return;
    setTitleDraft(proposal.before.title);
    setTagList([...proposal.before.tags]);
    setCatDraft([...proposal.before.categoryIds]);
    setProposal(null);
    setAcceptError(null);
  };
  const accept = async () => {
    if (!proposal || !onAcceptEnrichment) return;
    const patch = acceptPatch(proposal.s, drafts);
    if (!patch) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      await onAcceptEnrichment(patch);
      setProposal(null);
    } catch (e) {
      setAcceptError(e instanceof Error && e.message ? e.message : 'Could not save the suggestion.');
    } finally {
      setAccepting(false);
    }
  };
  const acceptable = proposal !== null && acceptPatch(proposal.s, drafts) !== null;
  const aiBadge = (field: 'title' | 'tags' | 'category') =>
    proposed.includes(field) ? (
      <span className="ml-2 inline-flex items-center gap-1 rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-900" data-testid={`ai-badge-${field}`}>
        <Sparkles className="h-3 w-3" aria-hidden /> AI suggested
      </span>
    ) : null;

  const isVideo = detail.mediaType === 'video';
  const videoReady = isVideo && detail.video.state === 'signed';
  const still = mediaImage(isVideo ? detail.poster : detail.preview, detail.lifecycle, detail.failureReason, detail.failureCode);
  const duration = formatDuration(detail.durationMs);
  const review = reviewLine(detail);
  const uploaded = formatAssetDate(detail.createdAt);
  const deleted = deletedLine(detail.deletedDaysLeft, audience);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="relative overflow-hidden rounded-md border border-border bg-muted">
        {onEnrich && !pending && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="absolute left-2 top-2 z-10"
            data-testid="ai-enrich-button"
            disabled={
              !canEnrich({ lifecycle: detail.lifecycle, thumbState: detail.thumb.state, deleted: detail.deletedDaysLeft !== null, running: scanning })
            }
            onClick={() => void runEnrich()}
          >
            <Sparkles className="mr-1 h-4 w-4" aria-hidden />
            {scanning ? 'Scanning…' : 'Suggest with AI'}
          </Button>
        )}
        {/* THE SCAN IS HONEST ABOUT WAITING, NOT ABOUT WORKING: it exists exactly while the request is in flight,
            so a slow call scans for as long as it takes and a fast one does not outlast its answer. */}
        {scanning && (
          <div className="asset-scan pointer-events-none absolute inset-0 z-[5]" data-testid="ai-scan" role="status" aria-label={SCAN_LABEL}>
            <span className="sr-only">{SCAN_LABEL}</span>
          </div>
        )}
        {videoReady && detail.video.state === 'signed' ? (
          <video
            controls
            preload="metadata"
            className="max-h-[75vh] w-full bg-black"
            src={detail.video.url}
            poster={detail.poster.state === 'signed' ? detail.poster.url : undefined}
            onError={onImageError}
          />
        ) : (
          <div className="flex max-h-[75vh] min-h-64 items-center justify-center">
            <AssetImage image={still} alt={assetDisplayName(detail)} onImageError={onImageError} className="object-contain" />
          </div>
        )}
      </div>

      <div className="space-y-4 text-sm">
        <div className="space-y-1">
          <AssetStatusBadge tile={detail} />
          {deleted && (
            <p className="text-foreground" data-testid="asset-deleted-line">
              {deleted}
            </p>
          )}
          {review && <p className="text-foreground">{review}</p>}
          {detail.review.status === 'rejected' && detail.review.reason && (
            <p className="text-muted-foreground">Reason: {detail.review.reason}</p>
          )}
          {detail.lifecycle === 'failed' && (
            <p className="text-foreground">{detail.failureReason ?? "We couldn't process this file."}</p>
          )}
          {detail.lifecycle === 'awaiting_support' && (
            <p className="text-foreground" data-testid="asset-awaiting-support">{AWAITING_SUPPORT_DETAIL}</p>
          )}
        </div>

        {enrichError && (
          <p className="text-xs text-foreground" role="alert" data-testid="ai-enrich-error">{enrichError}</p>
        )}
        {proposal && (
          <div className="space-y-2 rounded-md border border-violet-300 bg-violet-50 p-3 text-violet-900" data-testid="ai-pending-banner">
            <p>{PENDING_BANNER}</p>
            {keptNote(proposal.s) && <p className="text-xs text-violet-800">{keptNote(proposal.s)}</p>}
            {acceptError && <p className="text-xs" role="alert">{acceptError}</p>}
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={accepting || !acceptable} onClick={() => void accept()} data-testid="ai-accept">
                {accepting ? 'Saving…' : 'Accept'}
              </Button>
              {/* ⚠️ Pinned both colours: the outline variant's fill is the THEMED background (dark in dark mode) and its text inherits this panel's violet-900, so left alone it is dark on dark. Same "tinted surface pins both" rule as the panel — no dark: variants. */}
              <Button type="button" size="sm" variant="outline" className="border-violet-300 bg-white text-violet-900 hover:bg-violet-100 hover:text-violet-900" disabled={accepting} onClick={discard} data-testid="ai-discard">
                Discard
              </Button>
            </div>
          </div>
        )}

        {onSaveTitle && (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (parsedTitle.ok && !titleUnchanged) onSaveTitle(parsedTitle.text);
            }}
          >
            <label htmlFor="asset-title" className="block font-medium text-foreground">
              Title
              {aiBadge('title')}
            </label>
            <Input id="asset-title" value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} placeholder={detail.originalFilename} />
            <p className="text-xs text-muted-foreground">Shown instead of the file name. Leave it empty to use the file name.</p>
            {!parsedTitle.ok && <p className="text-xs text-foreground">{parsedTitle.reason}</p>}
            {titleError && <p className="text-xs text-foreground" role="alert">{titleError}</p>}
            <Button type="submit" size="sm" disabled={titleSaving || pending || !parsedTitle.ok || titleUnchanged}>
              {titleSaving ? 'Saving…' : 'Save title'}
            </Button>
          </form>
        )}

        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          {/* ⚠️ ALWAYS shown, titled or not: the file name is evidence of what was uploaded and is never replaced by a title. */}
          <dt className="text-muted-foreground">Original file</dt>
          <dd className="break-all text-foreground" data-testid="original-filename">{detail.originalFilename}</dd>
          <dt className="text-muted-foreground">Size</dt>
          <dd className="text-foreground">{formatBytes(detail.sizeBytes)}</dd>
          {detail.width !== null && detail.height !== null && (
            <>
              <dt className="text-muted-foreground">Dimensions</dt>
              <dd className="text-foreground">{detail.width} × {detail.height}</dd>
            </>
          )}
          {duration && (
            <>
              <dt className="text-muted-foreground">Length</dt>
              <dd className="text-foreground">{duration}</dd>
            </>
          )}
          <dt className="text-muted-foreground">Uploaded</dt>
          <dd className="text-foreground">{[detail.uploadedByName, uploaded].filter(Boolean).join(', ')}</dd>
        </dl>

        {detail.original.state === 'signed' && (
          <a href={detail.original.url} target="_blank" rel="noopener noreferrer" className="inline-block text-foreground underline">
            Open the original file
          </a>
        )}

        {onSaveCategories && categories && (
          <AssetCategoryPicker
            audience={audience}
            current={detail.categoryIds}
            state={categories}
            onSave={onSaveCategories}
            saving={categoriesSaving}
            saveError={categoriesError}
            draft={catDraft}
            onDraftChange={setCatDraft}
            saveDisabled={pending}
          />
        )}
        {proposed.includes('category') && (
          <p className="-mt-3">{aiBadge('category')}</p>
        )}

        {onSaveTags && (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!unchanged) onSaveTags(tagList);
            }}
          >
            <label htmlFor="asset-tags" className="block font-medium text-foreground">
              Tags
              {aiBadge('tags')}
            </label>
            <TagChipInput id="asset-tags" value={tagList} onChange={setTagList} disabled={saving} />
            {saveError && <p className="text-xs text-foreground" role="alert">{saveError}</p>}
            <Button type="submit" size="sm" disabled={saving || pending || unchanged}>
              {saving ? 'Saving…' : 'Save tags'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
